import React, { useState, useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "../../../../shared/config/googleMapsConfig";
import useTranslation from "../../../../shared/hooks/useTranslation";
import serviceProviderSettingsApi from "../../../serviceBoard/api/serviceProviderSettingsApi";
import { ServiceProviderSettings } from "../../../serviceBoard/models/models";
import RfsMapModal, { RfsMapData, ProviderLocation } from "./RfsMapModal";
import RfsOwnerMarker from "./RfsOwnerMarker";

interface RfsMiniMapProps {
  mapData?: RfsMapData | null;
}

const mapStyles = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

const RfsMiniMap = ({ mapData }: RfsMiniMapProps) => {
  const { translate } = useTranslation(["serviceBoard"]);
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1200px)");

  const [modalOpen, setModalOpen] = useState(false);
  const [providerSettings, setProviderSettings] = useState<ServiceProviderSettings | null>(null);

  const apiKey = (window as any).app?.config?.googleMapsApiKey || "";
  const { isLoaded } = useJsApiLoader({ id: GOOGLE_MAPS_SCRIPT_ID, googleMapsApiKey: apiKey, libraries: GOOGLE_MAPS_LIBRARIES });

  // Fetch provider settings on mount for distance calculation
  useEffect(() => {
    serviceProviderSettingsApi
      .getSettingsAsync()
      .then((settings) => setProviderSettings(settings))
      .catch(() => {
        // Silently fail — provider location just won't be shown
      });
  }, []);

  // Derive provider location from settings
  const providerLocation: ProviderLocation | null =
    providerSettings?.effectiveLatitude != null && providerSettings?.effectiveLongitude != null
      ? { lat: providerSettings.effectiveLatitude, lng: providerSettings.effectiveLongitude }
      : null;

  // Hide on phone
  if (isPhone) return null;

  const hasCoords =
    mapData != null &&
    !isNaN(mapData.ownerLat) &&
    !isNaN(mapData.ownerLng);

  const mapHeight = isTablet ? 140 : 180;

  if (!hasCoords || !mapData) {
    return (
      <Box
        sx={{
          height: mapHeight,
          width: "100%",
          borderRadius: "12px",
          border: `1px solid ${dt.cardBorder}`,
          backgroundColor: dt.inputBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <LocationOffOutlinedIcon sx={{ fontSize: 28, color: dt.textMuted, opacity: 0.5 }} />
        <Box
          sx={{
            fontSize: "12px",
            fontFamily: dt.font,
            fontWeight: 500,
            color: dt.textMuted,
          }}
        >
          {translate("serviceBoard:mapNoData")}
        </Box>
      </Box>
    );
  }

  const center = { lat: mapData.ownerLat, lng: mapData.ownerLng };

  const handleMapClick = () => {
    setModalOpen(true);
  };

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: mapHeight,
          width: "100%",
          borderRadius: "12px",
          border: `1px solid ${dt.cardBorder}`,
          backgroundColor: dt.inputBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ fontSize: "12px", fontFamily: dt.font, color: dt.textMuted }}>
          {translate("serviceBoard:mapLoading")}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: mapHeight,
          width: "100%",
          borderRadius: "12px",
          border: `1px solid ${dt.cardBorder}`,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          cursor: "pointer",
        }}
        onClick={handleMapClick}
      >
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={center}
          zoom={13}
          options={{
            styles: mapStyles,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "cooperative",
          }}
        >
          <RfsOwnerMarker
            position={center}
            companyName={mapData?.ownerName || ""}
            logoUrl={mapData?.ownerLogoUrl}
            priorityCode={mapData?.priorityTypeCode || 3}
            size={32}
          />
        </GoogleMap>
      </Box>

      {/* Map Modal -- opens on click */}
      <RfsMapModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mapData={mapData}
        providerLocation={providerLocation}
      />
    </>
  );
};

export default RfsMiniMap;
